// ============================================
// @narbodica/auth — onboarding/steps/SparkSteps.jsx
// ============================================
// Spark-specific onboarding steps.
// Note: identity verification (Stripe Identity)
// is required and non-skippable before the profile
// goes live. The profile is hidden until:
//   1. Identity verification confirmed, AND
//   2. At least 3 photos uploaded.
// ============================================

import { useState, useEffect } from 'react';

const ACCENT = '#8b5cf6';

const GENDER_PREFS = [
  { id: 'women', label: 'Women' },
  { id: 'men', label: 'Men' },
  { id: 'non-binary', label: 'Non-binary people' },
  { id: 'everyone', label: 'Everyone' },
];

const DEALBREAKER_OPTIONS = [
  'Smoking', 'Has children', 'Wants children', 'Pets', 'Drinks heavily',
  'Long distance', 'No job', 'Vegan/vegetarian only', 'Different religion',
  'Different politics', 'Age gap > 10 years',
];

const chip = (selected, accent = ACCENT) => ({
  padding: '10px 16px', borderRadius: 30, cursor: 'pointer',
  border: `2px solid ${selected ? accent : '#27272a'}`,
  background: selected ? `${accent}15` : '#18181b',
  color: selected ? '#fafafa' : '#71717a',
  fontSize: 14, fontWeight: selected ? 600 : 400,
  transition: 'all 0.2s', fontFamily: "'Inter', sans-serif",
});

// ─── Step: Identity Verification ────────────────────────────────────────────
function VerificationStep({ setCanProceed, accentColor, apiBase, token }) {
  const [status, setStatus] = useState('idle'); // idle | loading | pending | verified | failed
  const ac = accentColor || ACCENT;

  useEffect(() => {
    // Check if already verified
    if (token) {
      fetch(`${apiBase}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => {
          if (d.user?.isIdentityVerified) {
            setStatus('verified');
            setCanProceed(true);
          }
        }).catch(() => {});
    }
  }, [token]);

  const startVerification = async () => {
    setStatus('loading');
    try {
      const res = await fetch(`${apiBase}/api/identity/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.verificationUrl) {
        window.open(data.verificationUrl, '_blank');
        setStatus('pending');
      } else {
        setStatus('failed');
      }
    } catch {
      setStatus('failed');
    }
  };

  const checkStatus = async () => {
    setStatus('loading');
    try {
      const res = await fetch(`${apiBase}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      if (d.user?.isIdentityVerified) {
        setStatus('verified');
        setCanProceed(true);
      } else {
        setStatus('pending');
      }
    } catch {
      setStatus('pending');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {status === 'verified' ? (
        <div style={{ padding: 20, background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.2)', borderRadius: 14, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
          <p style={{ color: '#4ade80', fontSize: 15, fontWeight: 600, margin: '0 0 4px', fontFamily: "'Inter', sans-serif" }}>Identity verified</p>
          <p style={{ color: '#71717a', fontSize: 13, margin: 0, fontFamily: "'Inter', sans-serif" }}>Your account is verified and your profile can go live once you've added photos.</p>
        </div>
      ) : (
        <>
          <div style={{ padding: 20, background: '#18181b', border: '1px solid #27272a', borderRadius: 14 }}>
            <p style={{ color: '#a1a1aa', fontSize: 13, lineHeight: 1.7, margin: 0, fontFamily: "'Inter', sans-serif" }}>
              Spark requires identity verification to keep the community safe. Submit a government-issued ID and a short selfie — it takes about 2 minutes. Your documents are processed securely by our identity provider and are never stored by us.
            </p>
          </div>
          {status === 'pending' && (
            <div style={{ padding: 14, background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 12 }}>
              <p style={{ color: '#fbbf24', fontSize: 13, margin: 0, textAlign: 'center', fontFamily: "'Inter', sans-serif" }}>
                Verification in progress — usually takes a few minutes.
              </p>
            </div>
          )}
          {status === 'failed' && (
            <div style={{ padding: 14, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 12 }}>
              <p style={{ color: '#f87171', fontSize: 13, margin: 0, textAlign: 'center', fontFamily: "'Inter', sans-serif" }}>
                Something went wrong. Please try again.
              </p>
            </div>
          )}
          <button onClick={status === 'pending' ? checkStatus : startVerification}
            style={{ width: '100%', padding: 14, background: ac, border: 'none', borderRadius: 12, color: '#fff', fontSize: 14, fontWeight: 600, cursor: status === 'loading' ? 'wait' : 'pointer', fontFamily: "'Inter', sans-serif" }}>
            {status === 'loading' ? 'Loading...' : status === 'pending' ? 'I\'ve completed verification — check status' : 'Start verification'}
          </button>
        </>
      )}
    </div>
  );
}

// ─── Step: Dating Preferences ────────────────────────────────────────────────
function PreferencesStep({ data, setData, setCanProceed, accentColor }) {
  const ac = accentColor || ACCENT;
  const genderPref = data.genderPreference || [];
  const minAge = data.minAge || 18;
  const maxAge = data.maxAge || 45;
  const maxDist = data.maxDistance || 50;

  useEffect(() => { setCanProceed(genderPref.length > 0); }, [genderPref.length]);

  const toggleGender = (id) => {
    const next = genderPref.includes(id)
      ? genderPref.filter(g => g !== id)
      : id === 'everyone' ? ['everyone'] : genderPref.filter(g => g !== 'everyone').concat(id);
    setData({ ...data, genderPreference: next });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Gender preference */}
      <div>
        <p style={{ color: '#a1a1aa', fontSize: 13, fontWeight: 600, margin: '0 0 10px', fontFamily: "'Inter', sans-serif" }}>I'm interested in</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {GENDER_PREFS.map(g => (
            <div key={g.id} onClick={() => toggleGender(g.id)} style={chip(genderPref.includes(g.id), ac)}>{g.label}</div>
          ))}
        </div>
      </div>

      {/* Age range */}
      <div>
        <p style={{ color: '#a1a1aa', fontSize: 13, fontWeight: 600, margin: '0 0 10px', fontFamily: "'Inter', sans-serif" }}>
          Age range: <span style={{ color: '#fafafa' }}>{minAge} – {maxAge}</span>
        </p>
        <div style={{ display: 'flex', gap: 12 }}>
          {[['Min age', minAge, 18, maxAge - 1, v => setData({ ...data, minAge: v })],
            ['Max age', maxAge, minAge + 1, 80, v => setData({ ...data, maxAge: v })]].map(([label, val, min, max, onChange]) => (
            <div key={label} style={{ flex: 1 }}>
              <label style={{ color: '#52525b', fontSize: 11, fontFamily: "'Inter', sans-serif" }}>{label}</label>
              <input type="number" value={val} min={min} max={max} onChange={e => onChange(Number(e.target.value))}
                style={{ width: '100%', padding: '10px 12px', background: '#18181b', border: '1px solid #27272a', borderRadius: 10, color: '#fafafa', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: "'Inter', sans-serif" }} />
            </div>
          ))}
        </div>
      </div>

      {/* Distance */}
      <div>
        <p style={{ color: '#a1a1aa', fontSize: 13, fontWeight: 600, margin: '0 0 10px', fontFamily: "'Inter', sans-serif" }}>
          Max distance: <span style={{ color: '#fafafa' }}>{maxDist} km</span>
        </p>
        <input type="range" min={5} max={200} value={maxDist} onChange={e => setData({ ...data, maxDistance: Number(e.target.value) })}
          style={{ width: '100%', accentColor: ac }} />
      </div>
    </div>
  );
}

// ─── Step: About You (skippable) ─────────────────────────────────────────────
function AboutStep({ data, setData, setCanProceed }) {
  useEffect(() => { setCanProceed(true); }, []); // always skippable

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {[
        ['bio', 'About you', 'What should people know about you?', 'textarea', 300],
        ['occupation', 'Occupation', 'What do you do?', 'text', 60],
        ['education', 'Education', 'Highest level or institution', 'text', 80],
      ].map(([key, label, placeholder, type, max]) => (
        <div key={key}>
          <label style={{ color: '#a1a1aa', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6, fontFamily: "'Inter', sans-serif" }}>{label}</label>
          {type === 'textarea' ? (
            <textarea value={data[key] || ''} onChange={e => setData({ ...data, [key]: e.target.value })}
              placeholder={placeholder} maxLength={max} rows={3}
              style={{ width: '100%', padding: '12px 14px', background: '#18181b', border: '1px solid #27272a', borderRadius: 12, color: '#fafafa', fontSize: 14, resize: 'vertical', outline: 'none', boxSizing: 'border-box', fontFamily: "'Inter', sans-serif" }} />
          ) : (
            <input type="text" value={data[key] || ''} onChange={e => setData({ ...data, [key]: e.target.value })}
              placeholder={placeholder} maxLength={max}
              style={{ width: '100%', padding: '12px 14px', background: '#18181b', border: '1px solid #27272a', borderRadius: 12, color: '#fafafa', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: "'Inter', sans-serif" }} />
          )}
        </div>
      ))}
      <p style={{ color: '#3f3f46', fontSize: 11, margin: 0, fontFamily: "'Inter', sans-serif" }}>
        All optional — you can complete this in your profile settings later.
      </p>
    </div>
  );
}

// ─── Step: Dealbreakers (skippable) ──────────────────────────────────────────
function DealbreakersStep({ data, setData, setCanProceed }) {
  useEffect(() => { setCanProceed(true); }, []);
  const selected = data.dealbreakers || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <p style={{ color: '#71717a', fontSize: 13, margin: 0, lineHeight: 1.6, fontFamily: "'Inter', sans-serif" }}>
        These are hard filters — people with these traits won't appear in your matches. They're never shown to other users.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {DEALBREAKER_OPTIONS.map(d => {
          const sel = selected.includes(d);
          return (
            <div key={d} onClick={() => {
              if (sel) setData({ ...data, dealbreakers: selected.filter(x => x !== d) });
              else if (selected.length < 5) setData({ ...data, dealbreakers: [...selected, d] });
            }}
              style={{ ...chip(sel, '#ef4444'), borderColor: sel ? '#ef4444' : '#27272a', background: sel ? 'rgba(239,68,68,0.12)' : '#18181b', color: sel ? '#f87171' : '#71717a' }}>
              {d}
            </div>
          );
        })}
      </div>
      {selected.length >= 5 && (
        <p style={{ color: '#f87171', fontSize: 12, margin: 0, fontFamily: "'Inter', sans-serif" }}>Maximum 5 dealbreakers</p>
      )}
      <p style={{ color: '#3f3f46', fontSize: 11, margin: 0, fontFamily: "'Inter', sans-serif" }}>Optional — skip if you'd rather set these later.</p>
    </div>
  );
}

// ─── Public API ───────────────────────────────────────────────────────────────
export function getSparkOnboardingSteps() {
  return [
    {
      id: 'verification',
      label: 'Verify',
      title: 'Verify your identity',
      subtitle: 'Required to keep Spark safe. Takes about 2 minutes — we never store your documents.',
      required: true,
      component: (props) => <VerificationStep {...props} />,
    },
    {
      id: 'preferences',
      label: 'Preferences',
      title: 'Who are you looking for?',
      subtitle: 'Set your basic match preferences. You can refine these any time.',
      required: true,
      component: (props) => <PreferencesStep {...props} />,
    },
    {
      id: 'about',
      label: 'About you',
      title: 'Tell people about yourself',
      subtitle: 'All optional — you can fill this in later, but profiles with bios get significantly more matches.',
      required: false,
      component: (props) => <AboutStep {...props} />,
    },
    {
      id: 'dealbreakers',
      label: 'Dealbreakers',
      title: 'Any absolute dealbreakers?',
      subtitle: 'Up to 5. Hard filters — these people won\'t appear in your matches, and your dealbreakers are never shown to anyone.',
      required: false,
      component: (props) => <DealbreakersStep {...props} />,
    },
  ];
}
